import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, Users } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { TextField, SelectField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useResourceActions } from '@/hooks/useResourceActions';
import { useAcademicYearPicker, useCampusPicker } from '@/hooks/usePickers';
import { gradeOfferingsApi, sectionsApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { AcademicStatus, Section } from '@/types/domain';
import { orDash } from '@/utils/format';

const schema = z.object({
  gradeOfferingId: z.string().uuid('Select a grade offering'),
  code: z.string().min(1, 'Code is required').max(32),
  name: z.string().min(1, 'Name is required').max(100),
  displayOrder: z.coerce.number().int().min(0).max(100),
  capacity: z.coerce.number().int().min(1, 'Capacity must be at least 1').max(500),
});

type FormValues = z.infer<typeof schema>;

/**
 * Sections — class groups within a grade offering.
 *
 * The form picks an academic year and campus first, then narrows to the
 * offerings that actually exist for that combination. That cascade is not just
 * convenience: a section belongs to one offering, and choosing the year, campus
 * and grade independently is precisely how the previous design allowed
 * combinations that were never actually run.
 */
export function SectionsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const table = useTableQuery('displayOrder,asc');

  const [yearFilter, setYearFilter] = useState('');
  const [campusFilter, setCampusFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<AcademicStatus | ''>('');
  const [editing, setEditing] = useState<Section | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<Section | null>(null);

  const years = useAcademicYearPicker();
  const campuses = useCampusPicker();

  const params = {
    ...table.params,
    ...(yearFilter !== '' ? { academicYearId: yearFilter } : {}),
    ...(campusFilter !== '' ? { campusId: campusFilter } : {}),
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.sections.list(params),
    queryFn: () => sectionsApi.list(params),
  });

  const { statusMutation, deleteMutation, invalidateAll } = useResourceActions({
    invalidates: [queryKeys.sections.all, queryKeys.gradeOfferings.all],
    changeStatus: sectionsApi.changeStatus,
    remove: sectionsApi.remove,
    label: 'Section',
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { gradeOfferingId: '', code: '', name: '', displayOrder: 1, capacity: 30 },
  });

  // Dependent dropdowns: year and campus narrow the offering list.
  const [formYear, setFormYear] = useState('');
  const [formCampus, setFormCampus] = useState('');

  const formOfferings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({ picker: true, formYear, formCampus }),
    queryFn: () =>
      gradeOfferingsApi.list({
        page: 0,
        size: 100,
        status: 'ACTIVE',
        ...(formYear !== '' ? { academicYearId: formYear } : {}),
        ...(formCampus !== '' ? { campusId: formCampus } : {}),
      }),
    enabled: open && formYear !== '' && formCampus !== '',
  });

  // An offering chosen under the previous year or campus is no longer valid.
  useEffect(() => {
    if (editing === null) {
      form.setValue('gradeOfferingId', '');
    }
  }, [formYear, formCampus, editing, form]);

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      editing === null ? sectionsApi.create(values) : sectionsApi.update(editing.id, values),
    onSuccess: () => {
      toast.success(editing === null ? 'Section created' : 'Section updated');
      close();
      invalidateAll();
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  function openCreate() {
    setEditing(null);
    form.reset({ gradeOfferingId: '', code: '', name: '', displayOrder: 1, capacity: 30 });
    setFormYear(yearFilter);
    setFormCampus(campusFilter);
    setOpen(true);
  }

  function openEdit(section: Section) {
    setEditing(section);
    setFormYear(section.academicYearId);
    setFormCampus(section.campusId);
    form.reset({
      gradeOfferingId: section.gradeOfferingId,
      code: section.code,
      name: section.name,
      displayOrder: section.displayOrder,
      capacity: section.capacity,
    });
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setEditing(null);
  }

  const columns: Column<Section>[] = [
    {
      key: 'name',
      header: 'Section',
      sortKey: 'name',
      render: (section) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink">{section.name}</span>
          {section.historical && (
            <Badge tone="neutral">
              <Lock className="h-3 w-3" aria-hidden="true" />
              read-only
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'code',
      header: 'Code',
      sortKey: 'code',
      render: (section) => <span className="font-mono text-xs">{section.code}</span>,
    },
    { key: 'grade', header: 'Grade', render: (section) => section.gradeName },
    { key: 'campus', header: 'Campus', render: (section) => orDash(section.campusName) },
    { key: 'year', header: 'Academic year', render: (section) => section.academicYearName },
    {
      key: 'capacity',
      header: 'Capacity',
      sortKey: 'capacity',
      render: (section) => <span className="tabular-nums">{section.capacity}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (section) => <StatusBadge status={section.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (section) => (
        <div className="flex justify-end gap-1">
          {can(PERMISSIONS.SECTION_UPDATE) && !section.historical && (
            <>
              <Button size="sm" variant="ghost" onClick={() => openEdit(section)}>
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                loading={statusMutation.isPending}
                onClick={() =>
                  statusMutation.mutate({
                    id: section.id,
                    payload: { status: section.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' },
                  })
                }
              >
                {section.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
              </Button>
            </>
          )}
          {can(PERMISSIONS.SECTION_DELETE) && !section.historical && (
            <Button size="sm" variant="ghost" onClick={() => setDeleting(section)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  const offerings = formOfferings.data?.data ?? [];

  return (
    <>
      <ListPage
        title="Sections"
        description="Class groups within a grade offering. Each belongs to one academic year, so past structures stay intact."
        icon={<Users className="h-5 w-5" aria-hidden="true" />}
        action={can(PERMISSIONS.SECTION_CREATE) ? <Button onClick={openCreate}>New section</Button> : undefined}
        search={{ value: table.searchInput, onChange: table.setSearchInput, placeholder: 'Search sections…' }}
        filters={
          <>
            <FilterSelect
              label="Year"
              value={yearFilter}
              onChange={(value) => {
                setYearFilter(value);
                table.setPage(0);
              }}
              options={(years.data ?? []).map((year) => ({ value: year.id, label: year.name }))}
            />
            <FilterSelect
              label="Campus"
              value={campusFilter}
              onChange={(value) => {
                setCampusFilter(value);
                table.setPage(0);
              }}
              options={(campuses.data ?? []).map((campus) => ({ value: campus.id, label: campus.name }))}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                table.setPage(0);
              }}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
            />
          </>
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(section) => section.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No sections yet"
          emptyDescription="Create a grade offering first, then divide it into sections."
          emptyIcon={<Users className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <Modal
        open={open}
        title={editing === null ? 'New section' : `Edit ${editing.name}`}
        description="A section belongs to one grade offering, which fixes its year, campus and grade."
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={form.handleSubmit((values) => save.mutate(values))} loading={save.isPending}>
              Save
            </Button>
          </>
        }
      >
        <form className="space-y-4" noValidate onSubmit={form.handleSubmit((values) => save.mutate(values))}>
          {editing === null ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <SelectField
                  label="Academic year"
                  required
                  value={formYear}
                  onChange={(event) => setFormYear(event.target.value)}
                >
                  <option value="">Select a year…</option>
                  {(years.data ?? [])
                    .filter((year) => year.status === 'PLANNED' || year.status === 'ACTIVE')
                    .map((year) => (
                      <option key={year.id} value={year.id}>
                        {year.name}
                        {year.current ? ' (current)' : ''}
                      </option>
                    ))}
                </SelectField>

                <SelectField
                  label="Campus"
                  required
                  value={formCampus}
                  onChange={(event) => setFormCampus(event.target.value)}
                >
                  <option value="">Select a campus…</option>
                  {(campuses.data ?? []).map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.name}
                    </option>
                  ))}
                </SelectField>
              </div>

              <SelectField
                label="Grade offering"
                required
                disabled={formYear === '' || formCampus === ''}
                hint={
                  formYear === '' || formCampus === ''
                    ? 'Select a year and campus first'
                    : offerings.length === 0
                      ? 'No grades are offered at that campus for that year yet'
                      : undefined
                }
                error={form.formState.errors.gradeOfferingId?.message}
                {...form.register('gradeOfferingId')}
              >
                <option value="">Select a grade…</option>
                {offerings.map((offering) => (
                  <option key={offering.id} value={offering.id}>
                    {offering.grade.name}
                  </option>
                ))}
              </SelectField>
            </>
          ) : (
            // Moving a section between offerings is refused by the server: it is
            // the record of where its students actually sat.
            <div className="rounded-lg bg-surface-2 px-3 py-2.5 text-sm text-muted">
              <span className="font-medium text-ink">{editing.gradeName}</span> · {editing.campusName} ·{' '}
              {editing.academicYearName}
              <p className="mt-1 text-xs">
                A section cannot be moved to a different offering. Create one there instead.
              </p>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Code"
              required
              hint="Unique within the offering, e.g. A"
              error={form.formState.errors.code?.message}
              {...form.register('code')}
            />
            <TextField
              label="Name"
              required
              error={form.formState.errors.name?.message}
              {...form.register('name')}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Display order"
              type="number"
              min={0}
              max={100}
              error={form.formState.errors.displayOrder?.message}
              {...form.register('displayOrder')}
            />
            <TextField
              label="Capacity"
              type="number"
              min={1}
              max={500}
              required
              hint="Cannot be set below the number already enrolled."
              error={form.formState.errors.capacity?.message}
              {...form.register('capacity')}
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete section"
        message={
          deleting === null
            ? ''
            : `Delete ${deleting.name} from ${deleting.gradeName}? This is refused while students are enrolled in it.`
        }
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleting !== null) {
            deleteMutation.mutate(deleting.id, { onSettled: () => setDeleting(null) });
          }
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
